// src/store/api/adminsApi.ts
import type {
  CreateUserDto,
  PaginatedUserResponseDto,
  UpdateUserDto,
  UserStatsDto,
} from "../../types";
import type {UserFilterDto, UserResponseDto} from "../../types/users";
import {baseApi} from "./baseApi";

export const adminApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getAdmins: builder.query<PaginatedUserResponseDto, UserFilterDto>({
      query: (params) => ({
        url: "/users",
        params: {...params, role: "ADMIN"},
      }),
      transformResponse: (response: {data: PaginatedUserResponseDto}) => response.data,
      providesTags: (result) =>
        result
          ? [
              ...result.data.map(({id}) => ({type: "User" as const, id})),
              {type: "User", id: "ADMIN_LIST"},
            ]
          : [{type: "User", id: "ADMIN_LIST"}],
    }),

    // Get Admin by ID
    getAdminById: builder.query<UserResponseDto, number>({
      query: (id) => `/users/${id}`,
      transformResponse: (response: {data: UserResponseDto}) => response.data,
      providesTags: (result, error, id) => [{type: "User", id}],
    }),

    // Create Admin
    createAdmin: builder.mutation<UserResponseDto, CreateUserDto>({
      query: (data) => ({
        url: "/users",
        method: "POST",
        body: {...data, role: "ADMIN"}, // Force ADMIN role
      }),
      invalidatesTags: [
        {type: "User", id: "ADMIN_LIST"},
        {type: "User", id: "LIST"}, // Also invalidate general user list
      ],
    }),

    // Update Admin
    updateAdmin: builder.mutation<
      UserResponseDto,
      {id: number; data: UpdateUserDto}
    >({
      query: ({id, data}) => ({
        url: `/users/${id}`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: (result, error, {id}) => [
        {type: "User", id},
        {type: "User", id: "ADMIN_LIST"},
        {type: "User", id: "LIST"},
      ],
    }),

    // Delete Admin
    deleteAdmin: builder.mutation<{success: boolean; message: string}, number>({
      query: (id) => ({
        url: `/users/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, id) => [
        {type: "User", id},
        {type: "User", id: "ADMIN_LIST"},
        {type: "User", id: "LIST"},
      ],
    }),

    // Get Admin Stats
    getAdminStats: builder.query<UserStatsDto, void>({
      query: () => "/users/stats",
      providesTags: [{type: "User", id: "ADMIN_STATS"}],
    }),

    // Bulk Delete Admins
    bulkDeleteAdmins: builder.mutation<
      {success: boolean; message: string},
      number[]
    >({
      query: (ids) => ({
        url: "/users/admins/bulk-delete",
        method: "DELETE",
        body: {ids},
      }),
      invalidatesTags: [
        {type: "User", id: "ADMIN_LIST"},
        {type: "User", id: "LIST"},
      ],
    }),
  }),
});

export const {
  useGetAdminsQuery,
  useGetAdminByIdQuery,
  useCreateAdminMutation,
  useUpdateAdminMutation,
  useDeleteAdminMutation,
  useGetAdminStatsQuery,

  useBulkDeleteAdminsMutation,
} = adminApi;
